'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type { PreviewSelection } from '@/components/sources/SourcePreviewDrawer';
import type { NewsReference } from '@/api/news/types';
import { selectionForReference, type DocLink } from '@/lib/news/newsUtils';
import { sendGtmEvent } from '@/lib/gtm/gtm';

/**
 * Il pannello si scarica al primo clic su una citazione, non con la pagina: si
 * porta dietro react-markdown per la scheda del documento, e chi legge senza
 * aprire una fonte non ne ha bisogno. Nell'HTML del server non compare comunque.
 */
const SourcePreviewDrawer = dynamic(() => import('@/components/sources/SourcePreviewDrawer'), {
  ssr: false,
});

type OpenDoc = (link: DocLink, label?: string) => void;

const NewsDocPreviewContext = createContext<OpenDoc | null>(null);

/** Apre la scheda di una fonte citata. Fuori dal provider non fa nulla. */
export const useOpenNewsDoc = (): OpenDoc => useContext(NewsDocPreviewContext) ?? (() => {});

interface NewsDocPreviewProviderProps {
  slug: string;
  references: NewsReference[];
  children: React.ReactNode;
}

/**
 * Le fonti di un articolo si aprono come dentro la piattaforma: un clic sulla
 * citazione, e la scheda del documento arriva nel pannello laterale — lo stesso
 * di `/fonti`, aperto sull'articolo citato quando la citazione è puntuale.
 * Un solo pannello per pagina: lo usano il testo, l'elenco delle fonti e la
 * scheda del provvedimento in testa.
 */
export const NewsDocPreviewProvider = ({
  slug,
  references,
  children,
}: NewsDocPreviewProviderProps) => {
  const [selection, setSelection] = useState<PreviewSelection | null>(null);

  /*
   * `open` deve restare la stessa funzione per tutta la vita della pagina.
   * Finisce nei `components` di react-markdown: se cambia, react-markdown
   * rimonta da capo ogni titolo e ogni citazione del testo — ed è quello che
   * succedeva all'arrivo dell'articolo in diretta, con l'indice rimasto a
   * guardare i titoli vecchi, staccati dalla pagina. Le fonti le legge da un
   * ref, sempre aggiornato.
   */
  const latest = useRef({ slug, references });
  useEffect(() => {
    latest.current = { slug, references };
  }, [slug, references]);

  const open = useCallback<OpenDoc>((link, label) => {
    const { slug, references } = latest.current;
    const reference = references.find(candidate => candidate.public_id === link.publicId);

    sendGtmEvent({
      event: 'news_source_open',
      news_slug: slug,
      news_source: reference?.name ?? label ?? link.publicId,
    });

    setSelection(
      reference
        ? { ...selectionForReference(reference), anchor: link.anchor }
        : // Una citazione che l'elenco non conosce ancora (testo più fresco
          // dell'HTML): la scheda si apre lo stesso, l'intestazione arriva col documento
          {
            publicId: link.publicId,
            sourceId: '',
            sourceName: 'Fonte ufficiale',
            sourceInitials: '§',
            documentName: label ?? 'Documento',
            category: null,
            date: null,
            anchor: link.anchor,
          }
    );
  }, []);

  return (
    <NewsDocPreviewContext.Provider value={open}>
      {children}
      {selection && (
        <SourcePreviewDrawer selection={selection} onClose={() => setSelection(null)} />
      )}
    </NewsDocPreviewContext.Provider>
  );
};
